import React, { useEffect, useState } from "react";
import { Container, Alert, ConnectedAlert, ProgressBar } from "../../../components";
import { Text, Heading } from "@telia/teddy";
import { connect, useDispatch } from "react-redux";
import ApprovalChoices from "./ApprovalChoices";
import InformationBlocks from "./InformationBlocks";
import RejectReceipt from "./RejectReceipt";
import { REALM, Realm } from "../../../../../constants";
import { RootState } from "../../../../../models/rootState";
import {
    isOrderNotFound as isOrderNotFoundSelector,
    getOrderItemGuid,
    getErrorMessage,
    getRealm,
    getOrderRef,
    isAllowedToOrder,
} from "../../../app/state/selectors";
import { selectors as AUTH_SELECTORS } from "../../../authentication";
import {
    getBankIdErrorMessage,
    isBankIdVerified as isBankIdVerifiedSelector,
} from "../../../authentication/state/selectors";
import { VerificationDeliveryMethod } from "../../../checkout/consts";
import {
    approvalFromFormerLegal,
    rejectFromFormerLegal,
} from "../../../checkout/state/actions/transferOwnershipActions";
import {
    sendCode,
    verifyCodeTransferOwnershipExistingCompanyAdmin as verifyCode,
} from "../../../checkout/state/actions/verificationActions";
import {
    getTransferBreakageFee,
    getTransferNewLegalOwnerField,
    getTransferNewIndividualLegalOwnerFullName,
    getHasRejectedTransferOrder,
    getTransferDate,
    getTransferExistingOrganizationName,
    isExistingLegalOwnerVerified as isExistingLegalOwnerVerifiedSelector,
    getTransferExistingOrganizationNumber,
    getAdminNumber,
} from "../../../checkout/state/selectors/accountSelectors";
import { getOrganizationName, getOrganizationNumber } from "../../../checkout/state/selectors/businessDetailsSelectors";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import TEXT from "../../text-consts";
import "./LandingPage.less";

interface StateProps {
    adminNumber: string;
    bankIdCustomer?: string;
    brRegOrganizationName: string;
    brRegOrganizationNumber: string;
    breakageFee: number;
    existingLegalOwner?: string;
    hasRejectedTransferOrder: boolean;
    isAllowedToSubmit: boolean;
    isBankIdVerified: boolean;
    isExistingLegalOwnerVerified: boolean;
    isFetchingData: boolean;
    isOrderNotFound: boolean;
    logicalErrorMessage?: string;
    newLegalOwner: string;
    orderItemGuid: string;
    orderRef: string;
    organizationName: string;
    realm: Realm;
    shouldShowBankId: boolean;
    transferDate?: string;
    verifiedCompanyAdmin?: string;
}

type ConsentPageToB2BAndB2CProps = StateProps;

const ConsentPageToB2BAndB2C: React.FC<ConsentPageToB2BAndB2CProps> = ({
    // Redux (data)
    isAllowedToSubmit,
    isFetchingData,
    isOrderNotFound,
    organizationName,
    breakageFee,
    brRegOrganizationName,
    brRegOrganizationNumber,
    hasRejectedTransferOrder,
    logicalErrorMessage,
    realm,
    orderRef,
}) => {
    const dispatch = useDispatch();

    const isB2BTransfer = !!organizationName || realm === REALM.B2B;
    const [hasClickedRejected, setHasClickedRejected] = useState(false);

    useEffect(() => {
        if (hasRejectedTransferOrder) {
            setHasClickedRejected(true);
        }
    }, [hasRejectedTransferOrder]);

    if (isOrderNotFound) {
        return (
            <div className="consent-landing-page">
                <div className="consent-landing-page__heading-container">
                    <Heading mb="100" as="h1" variant="title-600">
                        {TEXT.CONSENT_FORMER_LEGAL.HEADING}
                    </Heading>
                </div>
                <Container size="medium">
                    <section className="consent-landing-page__order-not-found">
                        <Heading mb="100" as="h2" variant="title-200">
                            {TEXT.CONSENT_FORMER_LEGAL.ORDER_NOT_FOUND.HEADING}
                        </Heading>
                        <Text mb="100">{TEXT.CONSENT_FORMER_LEGAL.ORDER_NOT_FOUND.TEXT}</Text>
                        <div
                            className="paragraph"
                            dangerouslySetInnerHTML={{ __html: TEXT.CONSENT_FORMER_LEGAL.ORDER_NOT_FOUND.HELP_TEXT }}
                        />
                    </section>
                </Container>
            </div>
        );
    }

    if (hasClickedRejected) {
        return <RejectReceipt />;
    }

    return (
        <div className="consent-landing-page">
            <div className="consent-landing-page__heading-container">
                <Heading mb="100" as="h1" variant="title-600">
                    {TEXT.CONSENT_FORMER_LEGAL.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} backgroundColor="white" />
            <ConnectedAlert />
            {logicalErrorMessage && (
                <Container size="small">
                    <Alert kind="warning">{logicalErrorMessage}</Alert>
                </Container>
            )}
            {isAllowedToSubmit && (
                <Container size="medium">
                    <InformationBlocks.Ingress />

                    <ApprovalChoices
                        breakageFee={breakageFee}
                        isB2BTransfer={isB2BTransfer}
                        submitAction={() => {
                            dispatch(approvalFromFormerLegal(orderRef, brRegOrganizationName, brRegOrganizationNumber));
                        }}
                        rejectAction={(reasonText: string, reasonId: string) => {
                            dispatch(rejectFromFormerLegal(orderRef, reasonText, reasonId));
                        }}
                        sendPinAction={(phoneNumber: string, deliveryMethod: VerificationDeliveryMethod) => {
                            dispatch(sendCode(phoneNumber, deliveryMethod));
                        }}
                        verifyPinAction={(phoneNumber: string, pin: string) => {
                            dispatch(verifyCode(pin, phoneNumber));
                        }}
                    />
                </Container>
            )}
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    brRegOrganizationName: getOrganizationName(state) || getTransferExistingOrganizationName(state) || "",
    brRegOrganizationNumber: getOrganizationNumber(state) || getTransferExistingOrganizationNumber(state) || "",
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    orderItemGuid: getOrderItemGuid(state),
    isExistingLegalOwnerVerified: isExistingLegalOwnerVerifiedSelector(state),
    orderRef: getOrderRef(state) || "",
    shouldShowBankId: AUTH_SELECTORS.shouldShowBankId(state),
    breakageFee: getTransferBreakageFee(state),
    adminNumber: getAdminNumber(state),
    organizationName: getTransferNewLegalOwnerField(state, "organization", "name"),
    newLegalOwner: getTransferNewIndividualLegalOwnerFullName(state),
    hasRejectedTransferOrder: getHasRejectedTransferOrder(state),
    transferDate: getTransferDate(state),
    logicalErrorMessage: getErrorMessage(state) || getBankIdErrorMessage(state),
    realm: getRealm(state),
    isBankIdVerified: isBankIdVerifiedSelector(state),
    isAllowedToSubmit: isAllowedToOrder(state),
});

export default connect(mapStateToProps)(ConsentPageToB2BAndB2C);
